import { Text } from "@marketplace/ui";
import { type DataTableColumn } from "@marketplace/ui/data-table";
import { getRouteApi } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SellerListTableActions } from "../components/seller-list-table-actions";
import { SellerStatusBadge } from "../components/seller-status-badge";
import type { SellerListItem } from "../types";

const route = getRouteApi("/users/sellers/");

export const useSellerListTableColumns = () => {
  const { page } = route.useSearch();
  const { t } = useTranslation("users");

  const sellerListTableColumns = useMemo<DataTableColumn<SellerListItem>[]>(
    () => [
      {
        accessor: "no",
        title: "No",
        render: (_, index) => (page - 1) * 10 + index + 1
      },
      {
        accessor: "name",
        title: t("sellerListPage.sellerListTable.columns.sellerName"),
        render: (records) => {
          return <div>{`${records.first_name} ${records.last_name}`}</div>;
        }
      },
      {
        accessor: "email",
        title: t("sellerListPage.sellerListTable.columns.sellerEmail")
      },
      {
        accessor: "company",
        title: t("sellerListPage.sellerListTable.columns.company"),
        render: (record) => record.attributes?.company_name || "-"
      },
      {
        accessor: "seller_type",
        title: t("sellerListPage.sellerListTable.columns.type"),
        render: ({ attributes }) => {
          const type = attributes?.seller_type;
          if (!type) return "-";

          const isInternal = type === "internal";

          return (
            <div
              className={`flex justify-center items-center w-[120px] h-6 rounded ${isInternal ? "bg-[#F2F5FF]" : "bg-[#F1FDFF]"}`}
            >
              <Text color={isInternal ? "#0648FF" : "#30B0C7"}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </Text>
            </div>
          );
        }
      },
      {
        accessor: "status",
        title: t("sellerListPage.sellerListTable.columns.status"),
        render: (record) => (
          <SellerStatusBadge status={record.attributes?.status} />
        )
      },
      {
        accessor: "role",
        title: t("sellerListPage.sellerListTable.columns.role"),
        render: () => "Seller"
      },
      {
        accessor: "actions",
        title: "",
        render: (record) => <SellerListTableActions sellerData={record} />
      }
    ],
    [t, page]
  );

  return { columns: sellerListTableColumns };
};
